import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { AxiosError, AxiosResponse } from "axios"
import qs from "qs"
import { useQuery } from "react-query"
import {
  IDashboardBehaviors,
  IDashboardBehaviorSelection,
  IDashboardBehaviorSeries,
  IDashboardClassSummary,
  IDashboardKpi,
  IDashboardPeriodParams,
  IDashboardStoreItems,
  IDashboardStudentAttention,
  IDashboardStudentKpi,
  IDashboardStudents,
  IDashboardStudentsParams,
  IDashboardTeachers,
  IDashboardTeachersKpi,
  IDashboardTeachersParams,
} from "ts/interfaces/AdminDashboard"
import {
  DashboardBehaviorType,
  DashboardStudentAttentionView,
} from "ts/types/AdminDashboard"

// The app default turns focus refetch off; the dashboard turns it back on.
export const DASHBOARD_QUERY_OPTIONS = {
  refetchInterval: 10 * 60 * 1000,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  keepPreviousData: true,
}

export const useQueryAdminDashboardKpi = (params: IDashboardPeriodParams) =>
  useQuery<AxiosResponse<IDashboardKpi>, AxiosError>(
    ["adminDashboardKpi", params],
    async () => axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_KPI, { params }),
    DASHBOARD_QUERY_OPTIONS,
  )

export const useQueryAdminDashboardClassSummary = (
  params: IDashboardPeriodParams,
) =>
  useQuery<AxiosResponse<IDashboardClassSummary>, AxiosError>(
    ["adminDashboardClassSummary", params],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_CLASS_SUMMARY, {
        params: { ...params, group_by: "class" },
      }),
    DASHBOARD_QUERY_OPTIONS,
  )

// With a selection, BED returns that behaviour's series; the title goes out
// verbatim (BED trims and ignores case).
export const useQueryAdminDashboardBehaviorSeries = (
  params: IDashboardPeriodParams,
  selection?: IDashboardBehaviorSelection | null,
) =>
  useQuery<AxiosResponse<IDashboardBehaviorSeries>, AxiosError>(
    ["adminDashboardBehaviorSeries", params, selection ?? null],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_BEHAVIORS_SERIES, {
        params: selection
          ? { ...params, type: selection.type, title: selection.title }
          : params,
      }),
    DASHBOARD_QUERY_OPTIONS,
  )

export const useQueryAdminDashboardBehaviors = (
  params: IDashboardPeriodParams,
  type: DashboardBehaviorType,
  page: { skip: number; limit: number },
  options?: { enabled?: boolean },
) =>
  useQuery<AxiosResponse<IDashboardBehaviors>, AxiosError>(
    ["adminDashboardBehaviors", params, type, page],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_BEHAVIORS, {
        params: { ...params, type, ...page },
      }),
    { ...DASHBOARD_QUERY_OPTIONS, ...options },
  )

export const useQueryAdminDashboardStoreItems = (
  params: IDashboardPeriodParams,
) =>
  useQuery<AxiosResponse<IDashboardStoreItems>, AxiosError>(
    ["adminDashboardStoreItems", params],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_STORE_ITEMS, { params }),
    DASHBOARD_QUERY_OPTIONS,
  )

export const useQueryAdminDashboardTeachersKpi = (
  params: IDashboardPeriodParams,
) =>
  useQuery<AxiosResponse<IDashboardTeachersKpi>, AxiosError>(
    ["adminDashboardTeachersKpi", params],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_TEACHERS_KPI, { params }),
    DASHBOARD_QUERY_OPTIONS,
  )

export const useQueryAdminDashboardStudentKpi = (
  params: IDashboardPeriodParams,
) =>
  useQuery<AxiosResponse<IDashboardStudentKpi>, AxiosError>(
    ["adminDashboardStudentKpi", params],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_STUDENTS_KPI, { params }),
    DASHBOARD_QUERY_OPTIONS,
  )

export const useQueryAdminDashboardTeachers = (
  params: IDashboardPeriodParams,
  table: IDashboardTeachersParams,
) =>
  useQuery<AxiosResponse<IDashboardTeachers>, AxiosError>(
    ["adminDashboardTeachers", params, table],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_TEACHERS, {
        params: { ...params, ...table },
      }),
    DASHBOARD_QUERY_OPTIONS,
  )

export const useQueryAdminDashboardStudents = (
  params: IDashboardPeriodParams,
  table: IDashboardStudentsParams,
) =>
  useQuery<AxiosResponse<IDashboardStudents>, AxiosError>(
    ["adminDashboardStudents", params, table],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_STUDENTS, {
        params: { ...params, ...table },
        // BED reads `classes` only as repeated keys (`classes=1&classes=2`).
        paramsSerializer: (query) => qs.stringify(query, { indices: false }),
      }),
    DASHBOARD_QUERY_OPTIONS,
  )

// BED ignores `limit` here and returns up to 10 rows.
export const useQueryAdminDashboardStudentAttention = (
  params: IDashboardPeriodParams,
  view: DashboardStudentAttentionView,
) =>
  useQuery<AxiosResponse<IDashboardStudentAttention>, AxiosError>(
    ["adminDashboardStudentAttention", params, view],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_DASHBOARD_STUDENTS_ATTENTION, {
        params: { ...params, view },
      }),
    DASHBOARD_QUERY_OPTIONS,
  )
